import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { X, Camera, Move3d, RotateCw, Save, Eye, Maximize2, SlidersHorizontal } from 'lucide-react'
import { ModelViewer, type ModelSource } from '../ModelViewer'
import type { CameraState, ModelTransform } from '@/types/camera'
import type { Quaternion } from '@/utils/quaternion'
import { cameraStateToQuaternion, quaternionToCameraState } from '@/utils/cameraConversion'
import { toEulerCamera, toQuaternionCamera } from '@/utils/cameraConversion'
import { IDENTITY_QUATERNION, quaternionNormalize } from '@/utils/quaternion'
import type { SplatRenderer } from '../SplatViewer/splatv-renderer'
import { RotationGizmo } from './RotationGizmo'
import { cn } from '@/utils/cn'

interface ModelEditorModalProps {
  modelId: string
  modelUrl: string
  isPlyModel?: boolean
  sequenceInfo?: { frameUrls: string[]; fps: number; audioUrl?: string | null } | null
  /** Audio URL for single-file (.splatv / .ply) playback. Sequences pull
   *  their audio URL from sequenceInfo.audioUrl. */
  audioUrl?: string | null
  /** Fully resolved source (e.g. from resolveModelSource). When provided it
   *  wins over modelUrl / isPlyModel / sequenceInfo routing — needed for
   *  ClipGStream models, which those legacy props can't describe. */
  source?: ModelSource | null
  currentCamera?: CameraState
  currentTransform?: ModelTransform
  onSave: (camera: CameraState | undefined, transform: ModelTransform | undefined) => void
  onCancel: () => void
}

const DEFAULT_CAMERA: CameraState = {
  position: { x: 0, y: 0, z: 1 },
  quaternion: IDENTITY_QUATERNION
}

const DEFAULT_TRANSFORM: ModelTransform = {
  position: { x: 0, y: 0, z: 0 },
  rotation: { rx: 0, ry: 0, rz: 0 },
  scale: { x: 1, y: 1, z: 1 }
}

export function ModelEditorModal({
  modelId: _modelId,
  modelUrl,
  isPlyModel = false,
  sequenceInfo = null,
  audioUrl = null,
  source: sourceProp = null,
  currentCamera = DEFAULT_CAMERA,
  currentTransform = DEFAULT_TRANSFORM,
  onSave,
  onCancel
}: ModelEditorModalProps) {
  // A dynamic .4dms / .plyseq sequence must always go through DualgsViewer
  // even if the caller forgot to flip isPlyModel — SplatViewer can't decode
  // per-frame .ply URLs. Treat sequenceInfo as the source of truth and only
  // fall back to isPlyModel for single .ply files (no sequence).
  const source = useMemo<ModelSource>(() => sourceProp ?? {
    kind: sequenceInfo ? 'sequence' : isPlyModel ? 'ply' : 'single',
    modelUrl,
    audioUrl: audioUrl ?? null,
    sequenceInfo,
    dataUrl: null,
    // Legacy callers can't describe a scene layer; only a resolved source can.
    staticUrl: null,
    staticTransform: null,
  }, [sourceProp, sequenceInfo, isPlyModel, modelUrl, audioUrl])
  // Only SplatViewer exposes model-transform editing.
  const useDualgsViewer = source.kind !== 'single'
  // DualgsViewer doesn't expose model-transform editing, so force the Camera
  // tab and hide the Transform tab for that path.
  const [activeTab, setActiveTab] = useState<'camera' | 'transform'>('camera')
  const [camera, setCamera] = useState<CameraState>(() => {
    const initialCamera = currentCamera || DEFAULT_CAMERA
    return initialCamera
  })
  const [transform, setTransform] = useState<ModelTransform>(currentTransform || DEFAULT_TRANSFORM)
  const [viewerCamera, setViewerCamera] = useState<{
    position: [number, number, number]
    quaternion: Quaternion
  }>(() => {
    const cameraData = cameraStateToQuaternion(currentCamera || DEFAULT_CAMERA)
    return cameraData
  })
  
  const rendererRef = useRef<SplatRenderer | null>(null)
  const [showGizmo, setShowGizmo] = useState(false)
  const [gizmoMode, setGizmoMode] = useState<'translate' | 'rotate' | 'scale'>('translate')
  const [isDraggingGizmo, setIsDraggingGizmo] = useState(false)
  const [selectedGizmoAxis, setSelectedGizmoAxis] = useState<string | null>(null)
  const isSettingCameraRef = useRef(false)
  const [hasViewerInitialized, setHasViewerInitialized] = useState(false)
  
  // Rotation control mode: 'sliders' or 'gizmo'
  const [rotationMode, setRotationMode] = useState<'sliders' | 'gizmo'>('gizmo')
  
  // Separate quaternion state for gizmo mode (Solution 3)
  const [gizmoQuaternion, setGizmoQuaternion] = useState<Quaternion>(() => {
    const quatCamera = toQuaternionCamera(currentCamera || DEFAULT_CAMERA)
    return quatCamera.quaternion
  })
  
  // Track the current viewer camera position separately
  const [currentViewerPosition, setCurrentViewerPosition] = useState<[number, number, number]>(
    cameraStateToQuaternion(currentCamera || DEFAULT_CAMERA).position
  )
  const [currentViewerQuaternion, setCurrentViewerQuaternion] = useState<Quaternion>(
    cameraStateToQuaternion(currentCamera || DEFAULT_CAMERA).quaternion
  )
  
  // For UI display, convert camera to Euler angles
  const eulerCamera = toEulerCamera(camera)
  

  // Update viewer when sliders change
  useEffect(() => {
    const { position, quaternion } = cameraStateToQuaternion(camera)
    setViewerCamera({ position, quaternion })
    // Also update the renderer directly to ensure sync
    if (hasViewerInitialized && rendererRef.current && !isSettingCameraRef.current) {
      isSettingCameraRef.current = true
      rendererRef.current.setCameraWithQuaternion(position, quaternion)
      // Reset flag after a short delay
      setTimeout(() => {
        isSettingCameraRef.current = false
      }, 100)
    }
  }, [camera, hasViewerInitialized])
  
  // Sync gizmo quaternion changes back to camera state
  useEffect(() => {
    if (rotationMode === 'gizmo') {
      setCamera(prev => ({
        position: prev.position,
        quaternion: gizmoQuaternion
      }))
    }
  }, [gizmoQuaternion, rotationMode])
  
  // Update gizmo quaternion when switching modes or camera changes externally
  useEffect(() => {
    if (rotationMode === 'gizmo') {
      const quatCamera = toQuaternionCamera(camera)
      setGizmoQuaternion(quatCamera.quaternion)
    }
  }, [rotationMode])

  // Update model transform when it changes
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setModelTransform(transform)
      // Update gizmo position if visible
      if (showGizmo) {
        rendererRef.current.setGizmoPosition(transform.position)
      }
    }
  }, [transform, showGizmo])

  // Handle camera changes from viewer - just track them, don't auto-update camera state
  const handleCameraChange = useCallback((position: [number, number, number], quaternion: Quaternion) => {
    // Avoid feedback loop when we're setting camera programmatically
    if (!isSettingCameraRef.current) {
      setCurrentViewerPosition(position)
      setCurrentViewerQuaternion(quaternion)
    }
  }, [])

  const handleCanvasReady = useCallback((canvas: HTMLCanvasElement) => {
    // Get the renderer from canvas
    const renderer = (canvas as any).__splatRenderer
    if (renderer) {
      rendererRef.current = renderer
      // Apply initial transform
      renderer.setModelTransform(transform)
      // Apply initial camera if we have one
      if (currentCamera) {
        const { position, quaternion } = cameraStateToQuaternion(currentCamera)
        isSettingCameraRef.current = true
        renderer.setCameraWithQuaternion(position, quaternion)
        // Update tracked position
        setCurrentViewerPosition(position)
        setCurrentViewerQuaternion(quaternion)
        setTimeout(() => {
          isSettingCameraRef.current = false
        }, 100)
      }
    }
  }, [transform, currentCamera])

  const handleUseCurrentView = () => {
    // Capture the current viewer camera position and rotation
    // This returns a quaternion-based camera state
    const newCamera = quaternionToCameraState(currentViewerPosition, currentViewerQuaternion)
    setCamera(newCamera)
  }

  const handleResetCamera = () => {
    setCamera(DEFAULT_CAMERA)
  }

  const handleResetTransform = () => {
    setTransform(DEFAULT_TRANSFORM)
  }

  const handleSave = () => {
    // Always save as quaternion format
    const quaternionCamera = toQuaternionCamera(camera)
    onSave(quaternionCamera, transform)
  }
  
  const handleGizmoToggle = () => {
    setShowGizmo(!showGizmo)
    if (rendererRef.current) {
      rendererRef.current.setGizmoVisible(!showGizmo)
      if (!showGizmo && transform) {
        // Update gizmo position when showing
        rendererRef.current.setGizmoPosition(transform.position)
      }
    }
  }
  
  const handleGizmoModeChange = (mode: 'translate' | 'rotate' | 'scale') => {
    setGizmoMode(mode)
    if (rendererRef.current) {
      rendererRef.current.setGizmoMode(mode)
    }
  }
  
  // Handle mouse events for gizmo interaction
  useEffect(() => {
    if (!showGizmo || !rendererRef.current) return
    
    const canvas = (rendererRef.current as any).canvas as HTMLCanvasElement
    if (!canvas) return
    
    const handleMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      const axis = rendererRef.current?.intersectGizmo(x, y)
      if (axis) {
        setSelectedGizmoAxis(axis)
        setIsDraggingGizmo(true)
        e.preventDefault()
        e.stopPropagation()
      }
    }
    
    const handleMouseMove = (_e: MouseEvent) => {
      if (!isDraggingGizmo || !selectedGizmoAxis) return
      
      // TODO: Implement gizmo dragging logic
      // This would calculate the movement based on the selected axis
      // and update the transform accordingly
    }
    
    const handleMouseUp = () => {
      setIsDraggingGizmo(false)
      setSelectedGizmoAxis(null)
    }
    
    canvas.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [showGizmo, isDraggingGizmo, selectedGizmoAxis, transform])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Model Editor</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6">
          <button
            onClick={() => setActiveTab('camera')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'camera'
                ? 'text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Camera size={18} />
              Camera
            </div>
            {activeTab === 'camera' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
            )}
          </button>
          {!useDualgsViewer && (
            <button
              onClick={() => setActiveTab('transform')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                activeTab === 'transform'
                  ? 'text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Move3d size={18} />
                Transform
              </div>
              {activeTab === 'transform' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex min-h-0">
          {/* Viewer */}
          <div className="flex-1 p-6">
            <div className="h-full bg-gray-100 rounded-lg overflow-hidden">
              <ModelViewer
                source={source}
                className="w-full h-full"
                cameraPosition={viewerCamera.position}
                cameraQuaternion={viewerCamera.quaternion}
                onCameraChange={handleCameraChange}
                dualgs={{
                  showUi: false,
                  showTimeline: !!source.sequenceInfo,
                }}
                splat={{
                  controls: true,
                  quality: 'high',
                  defaultCamera: currentCamera,
                  cameraPosition: hasViewerInitialized ? viewerCamera.position : undefined,
                  cameraQuaternion: hasViewerInitialized ? viewerCamera.quaternion : undefined,
                  onCanvasReady: handleCanvasReady,
                  onLoad: () => {
                    setHasViewerInitialized(true)
                    // Trigger a camera update to apply the initial camera position
                    setTimeout(() => {
                      setCamera(prev => ({ ...prev }))
                    }, 100)
                  },
                  autoFocus: false,
                }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="w-96 p-6 border-l overflow-y-auto">
            {activeTab === 'camera' ? (
              <div className="space-y-6">
                {/* Position Controls */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Camera Position</h3>
                  <div className="space-y-3">
                    {(['x', 'y', 'z'] as const).map((axis) => (
                      <div key={axis}>
                        <label className="text-xs text-gray-600 flex justify-between">
                          <span>{axis.toUpperCase()} Position</span>
                          <span>{camera.position[axis].toFixed(2)}</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="range"
                            min="-10"
                            max="10"
                            step="0.01"
                            value={camera.position[axis]}
                            onChange={(e) => setCamera({
                              ...camera,
                              position: { ...camera.position, [axis]: parseFloat(e.target.value) }
                            })}
                            className="flex-1"
                          />
                          <input
                            type="number"
                            min="-100"
                            max="100"
                            step="0.01"
                            value={camera.position[axis]}
                            onChange={(e) => setCamera({
                              ...camera,
                              position: { ...camera.position, [axis]: parseFloat(e.target.value) || 0 }
                            })}
                            className="w-20 px-2 py-1 text-sm border rounded"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rotation Controls */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Camera Rotation</h3>
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setRotationMode('gizmo')}
                        className={cn(
                          "px-2 py-1 rounded text-xs font-medium transition-colors",
                          rotationMode === 'gizmo' 
                            ? "bg-white text-gray-900 shadow-sm" 
                            : "text-gray-600 hover:text-gray-900"
                        )}
                        title="Visual Rotation Gizmo"
                      >
                        <Move3d size={14} className="inline-block" />
                      </button>
                      <button
                        onClick={() => setRotationMode('sliders')}
                        className={cn(
                          "px-2 py-1 rounded text-xs font-medium transition-colors",
                          rotationMode === 'sliders' 
                            ? "bg-white text-gray-900 shadow-sm" 
                            : "text-gray-600 hover:text-gray-900"
                        )}
                        title="Rotation Sliders"
                      >
                        <SlidersHorizontal size={14} className="inline-block" />
                      </button>
                    </div>
                  </div>
                  
                  {rotationMode === 'gizmo' ? (
                    <div className="space-y-3">
                      <RotationGizmo
                        quaternion={gizmoQuaternion}
                        onChange={(q) => setGizmoQuaternion(quaternionNormalize(q))}
                        size={180}
                        className="mx-auto"
                      />
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Pitch (X)</span>
                          <span>{(eulerCamera.rotation.rx * 180 / Math.PI).toFixed(1)}°</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Yaw (Y)</span>
                          <span>{(eulerCamera.rotation.ry * 180 / Math.PI).toFixed(1)}°</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Roll (Z)</span>
                          <span>{(eulerCamera.rotation.rz * 180 / Math.PI).toFixed(1)}°</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[
                        { key: 'rx', label: 'Pitch (X Rotation)' },
                        { key: 'ry', label: 'Yaw (Y Rotation)' },
                        { key: 'rz', label: 'Roll (Z Rotation)' }
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="text-xs text-gray-600 flex justify-between">
                            <span>{label}</span>
                            <span>{(eulerCamera.rotation[key as keyof typeof eulerCamera.rotation] * 180 / Math.PI).toFixed(1)}°</span>
                          </label>
                          <input
                            type="range"
                            min={-Math.PI}
                            max={Math.PI}
                            step="0.01"
                            value={eulerCamera.rotation[key as keyof typeof eulerCamera.rotation]}
                            onChange={(e) => {
                              // Convert to Euler, update, then convert back to quaternion
                              const newEulerCamera = toEulerCamera(camera)
                              newEulerCamera.rotation[key as keyof typeof eulerCamera.rotation] = parseFloat(e.target.value)
                              setCamera(toQuaternionCamera(newEulerCamera))
                            }}
                            className="w-full"
                          />
                        </div>
                      ))}
                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <p className="text-xs text-yellow-800">
                          Note: Sliders may affect each other due to Gimbal Lock. 
                          Use the visual gizmo mode for more intuitive rotation control.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={handleUseCurrentView}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Camera size={16} />
                      <span>Capture Current View</span>
                    </button>
                    <button
                      onClick={handleResetCamera}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <RotateCw size={16} />
                      <span>Reset Camera</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Transform Position Controls */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Model Position</h3>
                  <div className="space-y-3">
                    {(['x', 'y', 'z'] as const).map((axis) => (
                      <div key={axis}>
                        <label className="text-xs text-gray-600 flex justify-between">
                          <span>{axis.toUpperCase()} Position</span>
                          <span>{transform.position[axis].toFixed(2)}</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="range"
                            min="-10"
                            max="10"
                            step="0.01"
                            value={transform.position[axis]}
                            onChange={(e) => setTransform({
                              ...transform,
                              position: { ...transform.position, [axis]: parseFloat(e.target.value) }
                            })}
                            className="flex-1"
                          />
                          <input
                            type="number"
                            min="-100"
                            max="100"
                            step="0.01"
                            value={transform.position[axis]}
                            onChange={(e) => setTransform({
                              ...transform,
                              position: { ...transform.position, [axis]: parseFloat(e.target.value) || 0 }
                            })}
                            className="w-20 px-2 py-1 text-sm border rounded"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Transform Rotation Controls */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Model Rotation</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'rx', label: 'X Rotation' },
                      { key: 'ry', label: 'Y Rotation' },
                      { key: 'rz', label: 'Z Rotation' }
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="text-xs text-gray-600 flex justify-between">
                          <span>{label}</span>
                          <span>{(transform.rotation[key as keyof typeof transform.rotation] * 180 / Math.PI).toFixed(1)}°</span>
                        </label>
                        <input
                          type="range"
                          min={-Math.PI}
                          max={Math.PI}
                          step="0.01"
                          value={transform.rotation[key as keyof typeof transform.rotation]}
                          onChange={(e) => setTransform({
                            ...transform,
                            rotation: { ...transform.rotation, [key]: parseFloat(e.target.value) }
                          })}
                          className="w-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Transform Scale Controls */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Model Scale</h3>
                  <div className="space-y-3">
                    {(['x', 'y', 'z'] as const).map((axis) => (
                      <div key={axis}>
                        <label className="text-xs text-gray-600 flex justify-between">
                          <span>{axis.toUpperCase()} Scale</span>
                          <span>{transform.scale[axis].toFixed(2)}</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="range"
                            min="0.1"
                            max="5"
                            step="0.01"
                            value={transform.scale[axis]}
                            onChange={(e) => setTransform({
                              ...transform,
                              scale: { ...transform.scale, [axis]: parseFloat(e.target.value) }
                            })}
                            className="flex-1"
                          />
                          <input
                            type="number"
                            min="0.01"
                            max="10"
                            step="0.01"
                            value={transform.scale[axis]}
                            onChange={(e) => setTransform({
                              ...transform,
                              scale: { ...transform.scale, [axis]: parseFloat(e.target.value) || 1 }
                            })}
                            className="w-20 px-2 py-1 text-sm border rounded"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={handleResetTransform}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <RotateCw size={16} />
                      <span>Reset Transform</span>
                    </button>
                  </div>
                </div>

                {/* Gizmo Controls */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Visual Gizmo</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleGizmoToggle}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        showGizmo
                          ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <Eye size={16} />
                      <span>{showGizmo ? 'Hide Gizmo' : 'Show Gizmo'}</span>
                    </button>
                    
                    {showGizmo && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleGizmoModeChange('translate')}
                          className={`flex-1 px-3 py-1.5 text-sm rounded transition-colors ${
                            gizmoMode === 'translate'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          title="Translate (T)"
                        >
                          <Move3d size={14} className="mx-auto" />
                        </button>
                        <button
                          onClick={() => handleGizmoModeChange('rotate')}
                          className={`flex-1 px-3 py-1.5 text-sm rounded transition-colors ${
                            gizmoMode === 'rotate'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          title="Rotate (R)"
                        >
                          <RotateCw size={14} className="mx-auto" />
                        </button>
                        <button
                          onClick={() => handleGizmoModeChange('scale')}
                          className={`flex-1 px-3 py-1.5 text-sm rounded transition-colors ${
                            gizmoMode === 'scale'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          title="Scale (S)"
                        >
                          <Maximize2 size={14} className="mx-auto" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg mt-6">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Tips</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                {activeTab === 'camera' ? (
                  <>
                    <li>• Move the camera in the viewer to find the best angle</li>
                    <li>• Click "Capture Current View" to save that position</li>
                    <li>• Or use the sliders for precise adjustments</li>
                    <li>• This will be the initial view when users open the model</li>
                  </>
                ) : (
                  <>
                    <li>• Adjust position to center the model properly</li>
                    <li>• Use rotation to fix axis alignment issues</li>
                    <li>• Scale uniformly for best results</li>
                    <li>• Changes are applied in real-time</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <Save size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}